import { clsx } from "clsx";
import { BellOff, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { timeAgo } from "@/lib/format";
import { LoadingGrid, StateView } from "@/components/ui/StateView";
import { useBookingPopupStore } from "@/store/booking-popup-store";
import { useMarkNotificationRead, useVisibleNotifications, type AppNotification } from "./useNotifications";

export function NotificationsPage() {
  const navigate = useNavigate();
  const notifications = useVisibleNotifications();
  const markNotificationRead = useMarkNotificationRead();
  const reopenBookingPopup = useBookingPopupStore((state) => state.reopen);
  const visibleNotifications = notifications.data ?? [];
  const unread = visibleNotifications.filter((notification) => !notification.isRead);

  const openNotification = (notification: AppNotification) => {
    if (!notification.isRead) markNotificationRead.mutate(notification.id);
    if (notification.bookingId) {
      if (notification.type === "NEW_BOOKING_REQUEST" && notifications.openBookingIds.has(notification.bookingId)) reopenBookingPopup(notification.bookingId);
      // Discount approvals are listed on the dashboard.
      navigate(notification.type === "DISCOUNT_REQUEST" ? "/" : "/bookings");
    }
  };

  if (notifications.isLoading) return <main className="page notifications-page"><LoadingGrid /></main>;
  if (notifications.isError) return <main className="page notifications-page"><StateView title="Couldn't load notifications" message="Check your internet connection and try again." action={() => void notifications.refetch()} /></main>;

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const groups = [
    { title: "Today", items: visibleNotifications.filter((notification) => new Date(notification.createdAt) >= startOfToday) },
    { title: "Earlier", items: visibleNotifications.filter((notification) => new Date(notification.createdAt) < startOfToday) },
  ].filter((group) => group.items.length);

  return (
    <main className="page notifications-page">
      <header className="page-head resource-head">
        <div>
          <h1>Notifications</h1>
          <p className="page-head__meta">{unread.length ? `${unread.length} unread` : "You're all caught up"}</p>
        </div>
        {unread.length > 0 && (
          <button type="button" className="button button--secondary" onClick={() => unread.forEach((notification) => markNotificationRead.mutate(notification.id))}>
            <CheckCheck size={16} /> Mark all read
          </button>
        )}
      </header>

      {!visibleNotifications.length ? (
        <StateView icon={BellOff} title="No notifications" message="Unread and recent booking requests and reminders show up here." />
      ) : (
        groups.map((group) => (
          <section key={group.title} className="notification-group" aria-labelledby={`notifications-${group.title}`}>
            <h2 id={`notifications-${group.title}`} className="section-label">{group.title}</h2>
            <div className="card notification-list">
              {group.items.map((notification) => (
                <button type="button" key={notification.id} className={clsx("notification-item", !notification.isRead && "notification-item--unread")} onClick={() => openNotification(notification)}>
                  <span className="notification-item__dot" aria-label={notification.isRead ? undefined : "Unread"} />
                  <span className="notification-item__body">
                    <strong>{notification.title}</strong>
                    <small>{notification.body}</small>
                    <small className="notification-item__time">{timeAgo(notification.createdAt)}</small>
                  </span>
                </button>
              ))}
            </div>
          </section>
        ))
      )}

    </main>
  );
}
